import { create } from 'zustand';

import { api, session } from './api/client';

export interface AdminOperator {
  id: string;
  code: string;
  name: string;
  role: string;
  roleLabel: string;
  mustChangePassword: boolean;
  permissions: string[];
}

interface AdminState {
  operator: AdminOperator | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  markPasswordChanged: () => void;
  can: (permission: string) => boolean;
}

export const useAdmin = create<AdminState>((set, get) => ({
  operator: null,
  isAuthenticated: false,
  isBootstrapping: true,

  async login(identifier, password) {
    const result = await api.login(identifier, password);
    session.set(result.accessToken, result.refreshToken);
    // El perfil completo trae la etiqueta del rol, que el login no incluye.
    const me = await api.me();
    set({ operator: me, isAuthenticated: true });
  },

  async logout() {
    try {
      await api.logout();
    } catch {
      /* la sesión local se cierra de todos modos */
    }
    session.clear();
    set({ operator: null, isAuthenticated: false });
  },

  async bootstrap() {
    set({ isBootstrapping: true });
    if (!session.accessToken) {
      set({ isBootstrapping: false, isAuthenticated: false, operator: null });
      return;
    }
    try {
      const me = await api.me();
      set({ operator: me, isAuthenticated: true });
    } catch {
      session.clear();
      set({ isAuthenticated: false, operator: null });
    } finally {
      set({ isBootstrapping: false });
    }
  },

  markPasswordChanged() {
    const operator = get().operator;
    if (operator) set({ operator: { ...operator, mustChangePassword: false } });
  },

  /**
   * El panel oculta lo que el API rechazaría. El mapa rol → permisos vive en
   * @capi/shared, así que las dos listas no pueden desincronizarse: ocultar un
   * botón que el servidor permitiría, o mostrar uno que va a fallar con 403,
   * son los dos errores que esto evita.
   */
  can(permission) {
    return get().operator?.permissions.includes(permission) ?? false;
  },
}));
